// ============================================================
// MANIC MEETER — Colleague generation
// ============================================================
// Names and catchphrases come from the active locale; talkiness and department
// wiring are balance, and stay here.

import { Colleague, ColleagueRole, Department } from "./types";
import { C } from "./i18n";

// Talkiness: how much fatigue per turn (1-10)
const ROLE_TALKINESS: Record<ColleagueRole, [number, number]> = {
  [ColleagueRole.FrontendDev]: [2, 4],
  [ColleagueRole.BackendDev]: [1, 3],
  [ColleagueRole.ClientManager]: [5, 8],
  [ColleagueRole.HRManager]: [4, 7],
  [ColleagueRole.MarketingGuru]: [6, 9],
  [ColleagueRole.Chef]: [7, 10],
  [ColleagueRole.Abteilungsleiter]: [5, 8],
  [ColleagueRole.Teamleiter]: [3, 6],
  [ColleagueRole.Designer]: [2, 5],
  [ColleagueRole.Praktikant]: [1, 2],
  [ColleagueRole.ScrumMaster]: [4, 7],
  [ColleagueRole.ProductOwner]: [5, 8],
  // Nobody can contradict a man they cannot understand, so he keeps going.
  [ColleagueRole.Sprachbarriere]: [6, 9],
};

/** Roles that can turn up by chance. The language barrier is placed, not rolled. */
const RANDOM_ROLES: ColleagueRole[] = Object.values(ColleagueRole)
  .filter((r) => r !== ColleagueRole.Sprachbarriere);

/** How often he is in the room. Often enough to be a running gag, rarely enough to stay one. */
const FOREIGN_COLLEAGUE_CHANCE = 0.22;
/** Below this headcount there is nowhere for him to hide. */
const FOREIGN_COLLEAGUE_MIN_PARTICIPANTS = 3;

function randInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

const usedNames = new Set<string>();

export function generateColleague(role?: ColleagueRole): Colleague {
  const chosenRole = role ?? pick(RANDOM_ROLES);
  const content = C();

  if (chosenRole === ColleagueRole.Sprachbarriere) {
    const foreign = content.foreignColleague;
    usedNames.add(foreign.name);
    const [minTalk, maxTalk] = ROLE_TALKINESS[chosenRole];
    return {
      name: foreign.name,
      role: chosenRole,
      department: getRoleDepartment(chosenRole),
      talkiness: randInt(minTalk, maxTalk),
      catchPhrase: pick(foreign.phrases),
      isCurrentlySpeaking: false,
    };
  }

  const firstNames = content.firstNames;
  let name: string;
  do {
    name = pick(firstNames);
  } while (usedNames.has(name) && usedNames.size < firstNames.length);
  usedNames.add(name);

  const dept = getRoleDepartment(chosenRole);
  const [minTalk, maxTalk] = ROLE_TALKINESS[chosenRole];

  return {
    name,
    role: chosenRole,
    department: dept,
    talkiness: randInt(minTalk, maxTalk),
    catchPhrase: pick(content.catchPhrases[chosenRole]),
    isCurrentlySpeaking: false,
  };
}

export function resetNamePool(): void {
  usedNames.clear();
}

function getRoleDepartment(role: ColleagueRole): Department {
  switch (role) {
    case ColleagueRole.FrontendDev:
    case ColleagueRole.Designer:
      return Department.Frontend;
    case ColleagueRole.BackendDev:
    case ColleagueRole.ScrumMaster:
      return Department.Backend;
    case ColleagueRole.ClientManager:
    case ColleagueRole.ProductOwner:
      return Department.Client;
    case ColleagueRole.HRManager:
      return Department.HR;
    case ColleagueRole.MarketingGuru:
      return Department.Marketing;
    case ColleagueRole.Chef:
    case ColleagueRole.Abteilungsleiter:
    case ColleagueRole.Teamleiter:
      return Department.Management;
    case ColleagueRole.Praktikant:
      return pick([
        Department.Frontend,
        Department.Backend,
        Department.Marketing,
      ]);
    case ColleagueRole.Sprachbarriere:
      // Nobody has ever established which department he reports into.
      return pick(Object.values(Department));
  }
}

export function generateColleagues(
  count: number,
  mustInclude?: ColleagueRole[],
): Colleague[] {
  const colleagues: Colleague[] = [];
  if (mustInclude) {
    for (const role of mustInclude) {
      colleagues.push(generateColleague(role));
    }
  }
  if (
    count >= FOREIGN_COLLEAGUE_MIN_PARTICIPANTS &&
    colleagues.length < count &&
    !colleagues.some((c) => c.role === ColleagueRole.Sprachbarriere) &&
    Math.random() < FOREIGN_COLLEAGUE_CHANCE
  ) {
    colleagues.push(generateColleague(ColleagueRole.Sprachbarriere));
  }
  while (colleagues.length < count) {
    colleagues.push(generateColleague());
  }
  return colleagues;
}
